import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Space, message } from 'antd';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import useService from '@/lib/hooks/useService';
import { detailTitles } from '../detailModalTitles';
import styles from './index.module.scss';
import type { CarServiceReservation } from '@/types/database/carService';
import type { ReservedUser } from '../../types';

interface CheckDetailButtonsWithModalProps {
  title: string;
  reservationId: string | undefined;
}

dayjs.extend(advancedFormat);

const CheckDetailButtonsWithModal = ({
  title,
  reservationId,
}: CheckDetailButtonsWithModalProps) => {
  const startOfTodayTimestamp = Number(dayjs().startOf('day').format('x'));
  const endOfTodayTimestamp = Number(dayjs().endOf('day').format('x'));

  const [todayReservations, , carService] = useService<CarServiceReservation>({
    collectionName: 'carService',
    query: {
      $and: [
        { reservationTime: { $gt: startOfTodayTimestamp } },
        { reservationTime: { $lt: endOfTodayTimestamp } },
      ],
    },
  });

  const [users] = useService<ReservedUser>({
    collectionName: 'users',
  });

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [reservations, setReservations] = useState<CarServiceReservation[]>([]);

  const getLatestReservations = useCallback(() => {
    carService
      .find({
        query: {
          $and: [
            { reservationTime: { $gt: startOfTodayTimestamp } },
            { reservationTime: { $lt: endOfTodayTimestamp } },
          ],
        },
      })
      .then(({ data }) => {
        setReservations(data);
      })
      .catch(() => {
        message.error('獲取最新資料失敗，請手動按F5重新整理');
      });
  }, [carService, endOfTodayTimestamp, startOfTodayTimestamp]);

  useEffect(() => {
    setReservations(todayReservations);
  }, [todayReservations]);

  useEffect(() => {
    getLatestReservations();
  }, [getLatestReservations, isOpenModal]);

  const reservationData = reservations.find(
    (reservation) => reservation._id === reservationId
  );

  const reservedUser = users.find(
    (user) => user._id === reservationData?.userId
  );

  const getModalContent = () => {
    if (reservationData) {
      const reservationForModalContent = {
        ...reservationData,
        createdTime: dayjs(reservationData.createdTime).format(
          'YYYY-MM-DD HH:mm'
        ),
        reservationTime: dayjs(reservationData.reservationTime).format(
          'YYYY-MM-DD HH:mm'
        ),
        actualCarTime: dayjs(reservationData.actualCarTime).format(
          'YYYY-MM-DD HH:mm'
        ),
        estimatedArrivalTime: dayjs(
          reservationData.estimatedArrivalTime
        ).format('YYYY-MM-DD HH:mm'),
        actualArrivalTime: dayjs(reservationData.actualArrivalTime).format(
          'YYYY-MM-DD HH:mm'
        ),
      };

      for (const key in reservationForModalContent) {
        if (reservationForModalContent[key] === 'Invalid Date') {
          reservationForModalContent[key] = '';
        }
      }

      const modalContents = detailTitles.map((titleObj) => {
        const { title, value } = titleObj;
        switch (title) {
          case '戶號':
            return {
              title,
              content: reservedUser?.householdNumbers[0],
            };

          case '住戶':
            return {
              title,
              content: reservedUser?.name,
            };

          default:
            return {
              title,
              content: reservationForModalContent[value],
            };
        }
      });
      return modalContents.map((content) => (
        <div key={content.title}>
          <h5 className={styles.modalContentTitle}>{content.title}</h5>
          <p>{content.content}</p>
        </div>
      ));
    } else {
      return [];
    }
  };

  return (
    <Space>
      <Modal
        open={isOpenModal}
        closable={false}
        onCancel={() => setIsOpenModal(false)}
        footer={
          <div className={styles.modalFooter}>
            <Button onClick={() => setIsOpenModal(false)}>確認</Button>
          </div>
        }
      >
        <h2 className={styles.modalHeader}>{title}</h2>
        <Space direction="vertical" size="middle">
          {getModalContent()}
        </Space>
      </Modal>
      <Button type="primary" onClick={() => setIsOpenModal(true)}>
        查看
      </Button>
    </Space>
  );
};

export default CheckDetailButtonsWithModal;
